import { IObstetricaLabResultsGroup, LabObservationObstetricalFile } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { Modal } from "@/Common/Components/Modals/Modal";
import { Patient } from "@/Common/Models/Patient/Patient";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { IHandleObstetricalLabResult } from "@/Patient/Components/MedicalNote/ObstetricalFile/ObstetricalLabResults/BaseObstetricalLabResultHandler";
import { ObstetricalLabResultHandlerFactory } from "@/Patient/Components/MedicalNote/ObstetricalFile/ObstetricalLabResults/ObstetricalLabResultHandlerFactory";
import { PreviousObstetricalPdfFormPopup } from "@/Patient/Components/MedicalNote/PreviousPregnancyPdfFormPopup";
import { FormDisplay } from "@/Patient/Models/Forms/FormDisplay";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { ObstetricalFileContributionType } from "@/Patient/Models/ObstetricalFile/Enum/ObstetricalFileContributionType";
import { ObstetricalFileType } from "@/Patient/Models/ObstetricalFile/Enum/ObstetricalFileType";
import { ObstetricalFileMedicalSummary } from "@/Patient/Models/ObstetricalFile/ObstetricalFileMedicalSummary";
import { ObstetricalFileModel } from "@/Patient/Models/ObstetricalFile/ObstetricalFileModel";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { IObstetricalFileProps } from "../ObstetricalFile";
import { BritishColumbiaObstetricalFile } from "./BritishColumbiaObstetricalFile";
import { OntarioObstetricalFile } from "./OntarioObstetricalFile";
import { QuebecObstetricalFile } from "./QuebecObstetricalFile";

interface IObstetricalFileControllerProps {
    noteModel: IPatientMedicalNoteModel;
    medicalSummaryModel: IPatientMedicalSummaryModel;
    patientModel: Patient;
    immunizations: MedicalItemImmunization[];
    isReadOnly: boolean;
    isEditMode?: boolean;
    isNotInteractive?: boolean;
    siteId: string;
}

class ObstetricalFileControllerState {
    public showPreviousObstetricalPdfFormPopup: boolean;
    public fetchLabObservations: () => Promise<IObstetricaLabResultsGroup[]>;
    public obstetricalFileLabObservations?: KeyValuePair<string, LabObservationObstetricalFile[]>[];
    public isFormExtended: boolean = false;

    public constructor(init?: Partial<ObstetricalFileControllerState>) {
        Object.assign(this, init);
    }

    public static getDefault(): ObstetricalFileControllerState {
        return new ObstetricalFileControllerState({
            showPreviousObstetricalPdfFormPopup: false,
            fetchLabObservations: null
        });
    }
}

class ObstetricalFileController extends BaseReactComponent<IObstetricalFileControllerProps, ObstetricalFileControllerState> {
    private obstetricalLabResultHandler: IHandleObstetricalLabResult;

    public constructor(props: IObstetricalFileControllerProps) {
        super(props);

        this.state = this.init();
    }

    public componentDidUpdate(prevProps: Readonly<IObstetricalFileControllerProps>): void {
        if (this.props.patientModel.IndividualResponse.Contact_id != prevProps.patientModel.IndividualResponse.Contact_id)
            this.setState(this.init());
    }

    // careful when you switch in between patients the state must be reset since the component will not reconstruct
    public init(): ObstetricalFileControllerState {
        const encounterSite = cacheStore.GetSiteById(this.props.siteId);
        const obstetricalFileType = this.getObstetricalFileType(encounterSite);
        this.obstetricalLabResultHandler = ObstetricalLabResultHandlerFactory.createForType(obstetricalFileType);

        let s = ObstetricalFileControllerState.getDefault();
        s.fetchLabObservations = async () => {
            let patientId = this.props.patientModel.IndividualResponse.Contact_id;
            patientStore.PatientLabObstetricObservations[patientId] ??= await this.obstetricalLabResultHandler.getlabs(this.props.patientModel.IndividualResponse.Contact_id);
            return patientStore.PatientLabObstetricObservations[patientId];
        };
        return s;
    }

    public render(): JSX.Element {
        return (this.enableObstetricalFileSection() && this.renderObstetricalFileSection());
    }

    private renderObstetricalFileSection(): JSX.Element {
        const model = this.props.noteModel;
        const encounter = model.get("Encounter");
        const sentFaxes = model?.get("SentFaxes") ?? {};
        const isEditMode = this.props.isEditMode ?? !this.props.isReadOnly;
        const isReadOnly = this.props.isReadOnly ?? !this.props.isEditMode;

        const obstetricalFileModel = this.getObstetricalFile();
        const hasObstetricalFile = this.hasObstetricalFile();
        const formDisplay = model.get("ObstetricalFileFormDisplay");

        const encounterSite = cacheStore.GetSiteById(this.props.siteId);

        const obstetricalFileType = this.getObstetricalFileType(encounterSite);
        const previousObstetricalFile = model.get("PreviousObstetricalFiles").firstOrDefault(p => p.ObstetricalFileType === obstetricalFileType);

        const shouldShowDeletePregnancyWarningPopup = model.get("ShowDeleteObstetricalFileWarningPopup");

        let obstetricalFileProps: IObstetricalFileProps = null;

        if (hasObstetricalFile) {
            obstetricalFileProps = {
                model: obstetricalFileModel,
                previousObstetricalFile: previousObstetricalFile,
                contributionId: obstetricalFileModel.ContributionId,
                patient: this.props.patientModel,
                encounter: encounter,
                sentFaxes: sentFaxes[obstetricalFileModel.ContributionId],
                obstetricalFileMedicalSummary: new ObstetricalFileMedicalSummary(this.props.medicalSummaryModel, this.props.immunizations),
                consultationDateAtStartOfObstetricalFile: obstetricalFileModel.ConsultationDateAtStartOfObstetricalFile,
                formDisplay: formDisplay,
                isReadOnly: isReadOnly,
                isEditMode: isEditMode,
                isNotInteractive: this.props.isNotInteractive,
                onDelete: () => sessionStore.dispatch(LocalPatientOperations.MedicalNote.ShowDeleteObstetricalFileWarningPopup, { shouldShow: true }),
                fetchLabObservations: this.state.fetchLabObservations,
                onFormDisplayChange: formDisplay => this.onFormDisplayChange(formDisplay),
                onFormExtend: formDisplay => this.onFormExtend(formDisplay),
                obstetricalFileLabObservations: this.state.obstetricalFileLabObservations,
                isFormExtended: (this.props.isReadOnly) ? this.state.isFormExtended : formDisplay.isExtended,
                isSecondScreen: this.props.isReadOnly
            };
        }

        return (
            <tr>
                <td className="c1 bottom-pad">
                    <strong>{cacheStore.GetString(this.getObstetricalFileLabel(obstetricalFileType))}</strong>
                </td>
                <td className="c2">
                    <div className="tb-row tb">
                        <div className="add-btn-column">
                            {isEditMode && !hasObstetricalFile &&
                                <a onClick={() => this.handleNewObstetricalFile(obstetricalFileType)}>[+]</a>
                            }
                        </div>
                        <div className="note-items-column">
                            {shouldShowDeletePregnancyWarningPopup && (
                                <Modal
                                    width={400}
                                    title={cacheStore.GetString("WarningLabel")}
                                    onClose={() => sessionStore.dispatch(LocalPatientOperations.MedicalNote.ShowDeleteObstetricalFileWarningPopup, { shouldShow: false })}
                                    footer={(
                                        <div className="p-footer text-right">
                                            <button
                                                className="btn btn-default push-right-5"
                                                onClick={() => sessionStore.dispatch(LocalPatientOperations.MedicalNote.ShowDeleteObstetricalFileWarningPopup, { shouldShow: false })}
                                            >
                                                {cacheStore.GetString("CancelLabel")}
                                            </button>
                                            <button
                                                className="btn btn-primary"
                                                onClick={() => sessionStore.dispatch(LocalPatientOperations.MedicalNote.ConfirmDeleteObtetricalFile, { encounterId: model.get("Encounter").Id, obstetricalFileContributionId: model.get("ObstetricalFileModel").ContributionId })}
                                            >
                                                {cacheStore.GetString("OKLabel")}
                                            </button>
                                        </div>
                                    )}
                                    showOverlay
                                >
                                    <p>{cacheStore.GetString("Generic_DeleteWarning")}</p>
                                </Modal>
                            )}
                            {this.state.showPreviousObstetricalPdfFormPopup && (
                                <PreviousObstetricalPdfFormPopup
                                    encounterId={this.props.noteModel.get("Encounter").Id}
                                    patientContactFacetId={this.props.patientModel.PatientResponse.ContactFacet_id}
                                    onContinue={() => this.mutateState(s => s.showPreviousObstetricalPdfFormPopup = false, () => this.createObstetricalFile(obstetricalFileType))}
                                    onCancel={() => this.mutateState(s => s.showPreviousObstetricalPdfFormPopup = false)}
                                />
                            )}
                            {isEditMode && !hasObstetricalFile && (
                                <div>
                                    <a onClick={() => this.handleNewObstetricalFile(obstetricalFileType)}>{cacheStore.GetString("NewPregnancyLabel")} </a>
                                    {!!previousObstetricalFile && (
                                        <span>
                                            <span style={{ paddingLeft: "10px", paddingRight: "10px" }}>|</span>
                                            <a onClick={() => this.handleObstetricalFileContribution(encounter.Id, previousObstetricalFile.ContributionId, obstetricalFileType, ObstetricalFileContributionType.FollowUp)}>{cacheStore.GetString("Follow-UpLabel")} </a>
                                        </span>
                                    )}
                                    {!!previousObstetricalFile && this.isOntarioObstetricalFileEnabledFor(encounterSite) && (
                                        <span>
                                            <span style={{ paddingLeft: "10px", paddingRight: "10px" }}>|</span>
                                            <a onClick={() => this.handleObstetricalFileContribution(encounter.Id, previousObstetricalFile.ContributionId, obstetricalFileType, ObstetricalFileContributionType.Postnatal)}>{cacheStore.GetString("ObstetricalFile_PostnatalLabel")} </a>
                                        </span>
                                    )}
                                </div>
                            )}
                            {(hasObstetricalFile && obstetricalFileType === ObstetricalFileType.QuebecForm) &&
                                <QuebecObstetricalFile {...obstetricalFileProps} />
                            }
                            {(hasObstetricalFile && obstetricalFileType === ObstetricalFileType.OntarioForm) &&
                                <OntarioObstetricalFile {...obstetricalFileProps} />
                            }
                            {(hasObstetricalFile && obstetricalFileType === ObstetricalFileType.BritishColumbiaForm) &&
                                <BritishColumbiaObstetricalFile {...obstetricalFileProps} />
                            }
                        </div>
                    </div>
                </td>
            </tr>
        );
    }

    private isAnyObstetricalFileEnabled(): boolean {
        return cacheStore.ClinicSettings.EnableQuebecObstetricalFile
            || cacheStore.ClinicSettings.EnableOntarioObstetricalFile;
    }

    private enableObstetricalFileSection(): boolean {
        const encounterSite = cacheStore.GetSiteById(this.props.siteId);

        return this.isQuebecObstetricalFileEnabledFor(encounterSite)
            || this.isOntarioObstetricalFileEnabledFor(encounterSite)
            || this.isBritishColumbiaObstetricalFileEnabledFor(encounterSite)
            || (this.hasObstetricalFile() && this.isAnyObstetricalFileEnabled());
    }

    private getObstetricalFile(): ObstetricalFileModel {
        return this.props.noteModel.get("ObstetricalFileModel");
    }

    private hasObstetricalFile(): boolean {
        return !!this.getObstetricalFile();
    }

    public getObstetricalFileType(encounterSite: SitesModel): ObstetricalFileType {
        if (this.hasObstetricalFile())
            return this.getObstetricalFile().ObstetricalFileType;

        if (this.isQuebecObstetricalFileEnabledFor(encounterSite))
            return ObstetricalFileType.QuebecForm;

        if (this.isOntarioObstetricalFileEnabledFor(encounterSite))
            return ObstetricalFileType.OntarioForm;

        if (this.isBritishColumbiaObstetricalFileEnabledFor(encounterSite))
            return ObstetricalFileType.BritishColumbiaForm;

        console.warn("The site configuration of this encounter does not allow for an obstetrical file.");
    }

    private isQuebecObstetricalFileEnabledFor(encounterSite: SitesModel): boolean {
        return cacheStore.ClinicSettings.EnableQuebecObstetricalFile
            && encounterSite?.isPreferredSiteAddressInProvince(CanadaState.Quebec);
    }

    private isOntarioObstetricalFileEnabledFor(encounterSite: SitesModel): boolean {
        return cacheStore.ClinicSettings.EnableOntarioObstetricalFile
            && encounterSite?.isPreferredSiteAddressInProvince(CanadaState.Ontario);
    }

    private isBritishColumbiaObstetricalFileEnabledFor(encounterSite: SitesModel): boolean {
        return cacheStore.ClinicSettings.EnableBritishColumbiaObstetricalFile
            && encounterSite?.isPreferredSiteAddressInProvince(CanadaState.BritishColumbia);
    }

    private handleObstetricalFileContribution(
        encounterId: string,
        previousObstetricalFileContributionId: string,
        obstetricalFileType: ObstetricalFileType,
        contributionType: ObstetricalFileContributionType
    ): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.ObstetricalFileContribution, {
            encounterId,
            previousObstetricalFileContributionId,
            obstetricalFileType: obstetricalFileType,
            contributionType
        });
    }

    private handleNewObstetricalFile(obstetricalFileType: ObstetricalFileType): void {
        const noteModel = this.props.noteModel;

        if (noteModel.get("HasPreviousObstetricalPdfForms") && !noteModel.get("HasObstetricalFilesInOtherEncounters")) {
            this.mutateState(s => s.showPreviousObstetricalPdfFormPopup = true);
            return;
        }

        this.createObstetricalFile(obstetricalFileType);
    }

    private createObstetricalFile(obstetricalFileType: ObstetricalFileType): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.NewObstetricalFile, { obstetricalFileType: obstetricalFileType });
    }

    private getObstetricalFileLabel(type: ObstetricalFileType): string {
        switch (type) {
            case ObstetricalFileType.OntarioForm:
                return "OntarioObstetricalFileLabel";

            case ObstetricalFileType.BritishColumbiaForm:
                return "BritishColumbiaObstetricalFileLabel";

            default:
                return "ObstetricalFileLabel";
        }
    }

    private onFormDisplayChange(formDisplay: FormDisplay): void {
        this.props.isReadOnly ?
            this.mutateState(s => s.isFormExtended = true) :
            sessionStore.dispatch(LocalPatientOperations.MedicalNote.UpdateObstetricalFileFormDisplay, { formDisplay });
    }

    private onFormExtend(formDisplay: FormDisplay): void {
        this.props.isReadOnly ?
            this.mutateState(s => s.isFormExtended = !s.isFormExtended) :
            sessionStore.dispatch(LocalPatientOperations.MedicalNote.UpdateObstetricalFileFormDisplay, { formDisplay });
    }
}

export { ObstetricalFileController };
